import React from "react"
import { observer } from "mobx-react-lite"
import moment from "moment"

import { useStore } from "@store"
import { QueryHistoryData } from "@framework/types/analytics"
import { LOCAL_TIME_FORMAT, US_DATE_FORMAT } from "@framework/constants/global"
import UsersBubbles from "@components/ui/UsersBubbles/UsersBubbles"
import UserCard from "@components/ui/UserCard/UserCard"
import Column from "@components/ui/BaseTable/Column"
import Button from "@components/ui/Button/Button"
import Text from "@components/ui/Typography/Text"
import Row from "@components/ui/BaseTable/Row"
import Icon from "@components/ui/Icon/Icon"
import Chip from "@components/ui/Chip/Chip"
import Rating from "@components/ui/Rating/Rating"
import { SolutionTypes } from "@framework/constants/search-results"
import useModal from "@components/modals/useModal"
import { ModalsTypes } from "@components/modals/constants"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/ui/Tooltip/v2/Tooltip"
import { capitalizeFirstLetter } from "@utils/textUtils"
import { renderWorkflowQuery, reportURLToId } from "@utils/workflow-report"
import mainRoutes from "@root/main.routes"
import { queryDocumentTypeToWorkbookTypeName } from "@framework/types/workbook-report"
import { getReportDescriptionByType } from "@framework/constants/workbook"

import styles from "./QueryHistoryTable.module.sass"

interface QueryHistoryTableRowProps {
  queryInfo: QueryHistoryData
  onAssignQuestionClick?: React.MouseEventHandler
}

const QueryHistoryTableRow: React.FC<QueryHistoryTableRowProps> = observer(
  ({ queryInfo, onAssignQuestionClick }) => {
    const { user, createdAt, rating, comment } = queryInfo

    const { restrictionsStore: access } = useStore()

    return (
      <Row>
        <QueryTypeCell
          queryInfo={queryInfo}
          onAssignQuestionClick={onAssignQuestionClick}
          key="query-type"
        />

        <QueryPreviewCell
          queryInfo={queryInfo}
          onAssignQuestionClick={onAssignQuestionClick}
          key="query"
        />

        <Column key="user">
          <UserCard
            metaInfo={user.email}
            fullName={user.name}
            avatarSrc={user.avatarUrl ?? ""}
          />
        </Column>

        <Column key="date">
          <div className={styles.centeredTableRow}>
            <Text variant="inherit">
              {moment(createdAt).format(US_DATE_FORMAT)}
              <br />
              {moment(createdAt).format(LOCAL_TIME_FORMAT)}
            </Text>
          </div>
        </Column>

        <Column key="feedback">
          <div className={styles.centeredTableRow}>
            <Tooltip placement="top" color="primary">
              <TooltipTrigger>
                <Rating value={rating ?? 3} className={styles.ratingWidget} />
              </TooltipTrigger>
              <TooltipContent>{comment || "No comment"}</TooltipContent>
            </Tooltip>
          </div>
        </Column>

        {access.canViewQuestions && (
          <AssignStatusCell
            queryInfo={queryInfo}
            onAssignQuestionClick={onAssignQuestionClick}
            key="assign_status"
          />
        )}
      </Row>
    )
  }
)

export default QueryHistoryTableRow

const QueryPreviewCell: React.FC<QueryHistoryTableRowProps> = observer(
  ({ queryInfo }) => {
    const modal = useModal(ModalsTypes.QUERY_ANSWER_MODAL)

    if (queryInfo.recordType === "question") {
      const { question, summary, user } = queryInfo

      const showQueryAnswerModal = () =>
        modal.showModal({
          answerSummary: summary ?? "",
          question,
          user,
        })

      const cellProps = summary
        ? {
            className: styles.clickable,
            onClick: showQueryAnswerModal,
          }
        : null

      return (
        <Column key="query" {...cellProps}>
          <Text variant="inherit">
            {capitalizeFirstLetter(question)}{" "}
            {cellProps != null && <Icon name="eye" inline />}
          </Text>
        </Column>
      )
    }

    if (queryInfo.recordType === "company_report") {
      const { args, documentType, reportUrl } = queryInfo

      const cellProps = reportUrl
        ? {
            className: styles.clickable,
            onClick: () => {
              const reportId = reportURLToId(reportUrl)

              if (!reportId) return

              window.open(mainRoutes.workbookReport(reportId), "_blank")
            },
          }
        : null

      return (
        <Column key="query" {...cellProps}>
          <Text variant="inherit">
            {renderWorkflowQuery(
              queryDocumentTypeToWorkbookTypeName(documentType),
              args
            )}{" "}
            {cellProps != null && <Icon name="external-link" inline />}
          </Text>
        </Column>
      )
    }

    return <Column key="query">-</Column>
  }
)

const AssignStatusCell: React.FC<QueryHistoryTableRowProps> = observer(
  ({ queryInfo, onAssignQuestionClick }) => {
    const {
      restrictionsStore: access,
      solutionsStore: { allSolutions },
    } = useStore()

    if (!access.canViewQuestions) return <Column />

    if (queryInfo.recordType === "company_report")
      return (
        <Column>
          <Text variant="inherit" color="text50Color">
            Non Assignable
          </Text>
        </Column>
      )

    const renderExperts = () => {
      const { experts, totalAnswered, totalAwaiting, productSolution } =
        queryInfo

      const canPostToExpert = access.canPostToExpert && !!onAssignQuestionClick

      if (experts?.length) {
        const isAnswered = Number(totalAnswered) > 0
        const isSingle = experts.length === 1

        return (
          <div>
            <Chip
              color={isAnswered ? "green" : "gold"}
              variant="rounded"
              uppercase
              onClick={
                isAnswered || !canPostToExpert
                  ? undefined
                  : onAssignQuestionClick
              }
            >
              {isAnswered
                ? isSingle
                  ? "Verified"
                  : `Verified (${totalAnswered})`
                : isSingle
                ? "Pending"
                : `${totalAwaiting} Waiting`}
            </Chip>
            <UsersBubbles
              size="small"
              users={experts}
              suffix={
                isSingle
                  ? experts[0]?.name?.trim() || "Deactivated User"
                  : `${experts.length} Experts`
              }
              className={styles.userCard}
            />
          </div>
        )
      }

      if (
        !canPostToExpert ||
        allSolutions?.find(
          (solution) => solution.key === SolutionTypes.PrivateWorkspace
        )?.id === productSolution.id
      ) {
        return "-"
      }

      return (
        <Button
          before={<Icon name="plus" />}
          onClick={onAssignQuestionClick}
          size="small"
          variant="outlined"
        >
          Assign
        </Button>
      )
    }

    return <Column>{renderExperts()}</Column>
  }
)

const QueryTypeCell: React.FC<QueryHistoryTableRowProps> = observer(
  ({ queryInfo }) => {
    const { recordType, documentType } = queryInfo

    if (recordType === "question") {
      return <Column>Search Query</Column>
    }

    const reportName = queryDocumentTypeToWorkbookTypeName(documentType)
    const reportDescription = reportName
      ? getReportDescriptionByType(reportName).displayName
      : documentType

    return <Column>{reportDescription}</Column>
  }
)
